import React, { useState } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { useLinkAccount, usePrivy } from '@towns/privy'
import { Box, Icon, IconButton, IconProps, Paragraph, Stack, Text } from '@ui'
import { ReauthenticateButton } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'

type LinkedAccount = NonNullable<ReturnType<typeof usePrivy>['user']>['linkedAccounts'][number]

export function PrivyIdentityPanel() {
    const { user, ready: privyReady, authenticated } = usePrivy()
    const { linkEmail, linkPhone, linkGoogle, linkApple, linkTwitter, linkFarcaster } =
        useLinkAccount({
            onSuccess: () => {
                popupToast(({ toast }) => (
                    <StandardToast.Success toast={toast} message="Account linked successfully" />
                ))
            },
            onError: (error) => {
                popupToast(({ toast }) => {
                    if (error === 'exited_link_flow') {
                        return
                    }
                    return (
                        <StandardToast.Error
                            toast={toast}
                            message="Failed to link account"
                            subMessage={error.toString()}
                        />
                    )
                })
            },
        })
    const linkedAccounts = user?.linkedAccounts?.filter((account) => {
        if (account.type === 'wallet') {
            return account.walletClientType !== 'privy'
        }
        return true
    })

    // Helper to check if a specific account type is already linked
    const isAccountTypeLinked = (type: string) => {
        return linkedAccounts?.some((account) => {
            if (type === 'google') {
                return account.type === 'google_oauth'
            }
            if (type === 'apple') {
                return account.type === 'apple_oauth'
            }
            if (type === 'twitter') {
                return account.type === 'twitter_oauth'
            }
            return account.type === type
        })
    }

    const linkTypes: {
        type: 'email' | 'phone' | 'google' | 'apple' | 'twitter' | 'farcaster'
        icon: IconProps['type']
        label: string
        action: () => void
    }[] = [
        {
            type: 'google',
            icon: 'google',
            label: 'Google',
            action: linkGoogle,
        },
        {
            type: 'twitter',
            icon: 'twitter',
            label: 'Twitter',
            action: linkTwitter,
        },
        {
            type: 'farcaster',
            icon: 'farcaster',
            label: 'Farcaster',
            action: linkFarcaster,
        },
        {
            type: 'apple',
            icon: 'apple',
            label: 'Apple',
            action: linkApple,
        },
        {
            type: 'email',
            icon: 'mail',
            label: 'Email',
            action: linkEmail,
        },
        {
            type: 'phone',
            icon: 'phone',
            label: 'Phone',
            action: linkPhone,
        },
    ]

    return (
        <Panel label="Linked Accounts">
            <Stack grow gap="lg" position="relative" overflow="auto">
                {!privyReady ? (
                    <ButtonSpinner />
                ) : !authenticated ? (
                    <Stack gap="sm">
                        <Stack gap="sm" background="level2" padding="md" rounded="sm">
                            <Text>Reauthenticate with Privy to view your linked accounts.</Text>
                        </Stack>
                        <ReauthenticateButton message="" />
                    </Stack>
                ) : (
                    <>
                        <Stack gap>
                            <Text strong>Linked accounts</Text>
                            {linkedAccounts ? (
                                linkedAccounts.map((account) => (
                                    <LinkedAccount
                                        key={account.firstVerifiedAt?.toString()}
                                        account={account}
                                        totalLinkedAccounts={linkedAccounts.length}
                                    />
                                ))
                            ) : (
                                <Stack centerContent padding>
                                    <ButtonSpinner />
                                </Stack>
                            )}
                        </Stack>
                        <Text strong>Link another account</Text>
                        <Stack gap="sm">
                            {linkTypes.map(({ type, icon, label, action }) => {
                                const isLinked = isAccountTypeLinked(type)
                                return (
                                    <PanelButton
                                        centerContent
                                        key={type}
                                        opacity={isLinked ? '0.5' : 'opaque'}
                                        cursor={isLinked ? 'not-allowed' : 'pointer'}
                                        disabled={isLinked}
                                        onClick={action}
                                    >
                                        <Icon type={icon} size="square_sm" />
                                        <Paragraph>{label}</Paragraph>
                                    </PanelButton>
                                )
                            })}
                        </Stack>
                    </>
                )}
            </Stack>
        </Panel>
    )
}

const LinkedAccount = ({
    account,
    totalLinkedAccounts,
}: {
    account: LinkedAccount
    totalLinkedAccounts: number
}) => {
    const canUnlink = totalLinkedAccounts > 1

    const {
        unlinkEmail,
        unlinkPhone,
        unlinkGoogle,
        unlinkApple,
        unlinkTwitter,
        unlinkFarcaster,
        unlinkWallet,
    } = usePrivy()

    const [isUnlinking, setIsUnlinking] = useState(false)

    const onUnlink = async () => {
        if (isUnlinking) {
            return
        }
        try {
            setIsUnlinking(true)
            if (account.type === 'email') {
                await unlinkEmail(account.address)
            } else if (account.type === 'phone') {
                await unlinkPhone(account.number)
            } else if (account.type === 'google_oauth') {
                await unlinkGoogle(account.subject)
            } else if (account.type === 'apple_oauth') {
                await unlinkApple(account.subject)
            } else if (account.type === 'twitter_oauth') {
                await unlinkTwitter(account.subject)
            } else if (account.type === 'farcaster' && account.fid) {
                await unlinkFarcaster(account.fid)
            } else if (account.type === 'wallet') {
                await unlinkWallet(account.address)
            }
            popupToast(({ toast }) => (
                <StandardToast.Success toast={toast} message="Account unlinked successfully" />
            ))
        } catch (error) {
            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Failed to unlink account"
                    subMessage={error instanceof Error ? error.message : String(error)}
                />
            ))
        } finally {
            setIsUnlinking(false)
        }
    }

    if (account.type === 'email') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="mail" size="square_sm" />
                <Text>Email {account.address}</Text>
            </LinkedAccountDisplay>
        )
    }
    if (account.type === 'phone') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="phone" size="square_sm" />
                <Text>Phone {account.number}</Text>
            </LinkedAccountDisplay>
        )
    }
    if (account.type === 'google_oauth') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="google" size="square_sm" />
                <Text>Google {account.email}</Text>
            </LinkedAccountDisplay>
        )
    }
    if (account.type === 'apple_oauth') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="apple" size="square_sm" />
                <Text>Apple {account.email}</Text>
            </LinkedAccountDisplay>
        )
    }

    if (account.type === 'twitter_oauth') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="twitter" size="square_sm" />
                <Text>Twitter {account.username}</Text>
            </LinkedAccountDisplay>
        )
    }

    if (account.type === 'farcaster') {
        return (
            <LinkedAccountDisplay
                canUnlink={canUnlink}
                isUnlinking={isUnlinking}
                onUnlink={onUnlink}
            >
                <Icon type="farcaster" size="square_sm" />
                <Text>Farcaster {account.username}</Text>
            </LinkedAccountDisplay>
        )
    }

    if (account.type === 'wallet') {
        // 'privy' walletClientType == embeddedWallet
        if (account.walletClientType !== 'privy') {
            return (
                <LinkedAccountDisplay
                    canUnlink={canUnlink}
                    isUnlinking={isUnlinking}
                    onUnlink={onUnlink}
                >
                    <Icon type="wallet" size="square_sm" />
                    <Text>Wallet {account.address}</Text>
                </LinkedAccountDisplay>
            )
        }
        return null
    }

    // fallback
    return (
        <LinkedAccountDisplay canUnlink={canUnlink} isUnlinking={isUnlinking} onUnlink={onUnlink}>
            <Icon type="profile" size="square_sm" />
            <Text>{account.type}</Text>
        </LinkedAccountDisplay>
    )
}

const LinkedAccountDisplay = ({
    children,
    canUnlink,
    onUnlink,
    isUnlinking,
}: {
    children: React.ReactNode
    canUnlink: boolean
    onUnlink: () => void
    isUnlinking: boolean
}) => {
    return (
        <Box horizontal gap alignItems="center" background="level2" padding="md" borderRadius="sm">
            <Box horizontal gap alignItems="center" style={{ flex: 1 }}>
                {children}
            </Box>
            {isUnlinking && <ButtonSpinner />}
            {!isUnlinking && canUnlink && <IconButton icon="unlink" onClick={onUnlink} />}
        </Box>
    )
}
